import { flattenSequentialMonadicQuestions, QUESTION_TYPES } from '@api/instant_survey';

import { SURVEY_QUESTION_FIELDS_TO_MERGE } from './constants';

const getMergedQuestion = ({ externalQuestion, fields, surveyQuestion }) => {
  const mergedFields = fields.reduce((acc, field) => {
    if (surveyQuestion?.[field] !== undefined) acc[field] = surveyQuestion[field];
    return acc;
  }, {});

  return { ...externalQuestion, ...mergedFields };
};

/**
 * Merges dashboard external-question metadata with Instant Survey question metadata.
 *
 * Dashboard response payloads are keyed by original source question ids. Most surveys,
 * including the Alchemer path, return external questions with those same source ids:
 *
 * @example
 * // Normal/source-id path:
 * // externalQuestion.id === surveyQuestion.questionSourceId === responseData key
 * mergeExternalQuestionsWithSurvey({
 *   externalQuestions: [{ id: 267418, title: 'Q1' }],
 *   surveyQuestions: [{ id: 347746, questionSourceId: 267418, text: 'Q1' }],
 * });
 * // => [{ id: 267418, questionSourceId: 267418, text: 'Q1', title: 'Q1' }]
 *
 * Exported Calypso surveys can return external questions with local/generated Instant Survey ids
 * instead. In that case, we intentionally match on `surveyQuestion.id` and normalize the merged
 * question back to `questionSourceId` so response lookups still use the backend response keys.
 *
 * @example
 * // Exported-Calypso local-id path:
 * // externalQuestion.id === surveyQuestion.id, but responseData is keyed by questionSourceId
 * mergeExternalQuestionsWithSurvey({
 *   externalQuestions: [{ id: 347746, title: 'Q1' }],
 *   surveyQuestions: [{ id: 347746, questionSourceId: 267418, text: 'Q1' }],
 * });
 * // => [{ id: 267418, questionSourceId: 267418, text: 'Q1', title: 'Q1' }]
 *
 * Sequential monadic questions are flattened after the same merge rules are applied to parent and
 * sub-question metadata.
 *
 * @param {Object} params
 * @param {Array<Object>} params.externalQuestions External questions used by the dashboard.
 * @param {Array<Object>} params.surveyQuestions Instant Survey questions for the same survey.
 * @returns {Array<Object>} Questions keyed for dashboard response lookups.
 */
export const mergeExternalQuestionsWithSurvey = ({ externalQuestions, surveyQuestions }) => {
  if (!Array.isArray(externalQuestions)) return [];

  const surveyQuestionsById = new Map();
  const surveyQuestionsBySourceId = new Map();
  const surveySubQuestionsById = new Map();
  const surveySubQuestionsBySourceId = new Map();
  (surveyQuestions || []).forEach((question) => {
    if (question?.id) {
      surveyQuestionsById.set(String(question.id), question);
    }

    if (question?.questionSourceId) {
      surveyQuestionsBySourceId.set(String(question.questionSourceId), question);
    }

    if (question?.type !== QUESTION_TYPES.SEQUENTIAL_MONADIC.value) return;

    (question.subQuestions || []).forEach((subQuestion) => {
      if (subQuestion?.id) {
        surveySubQuestionsById.set(String(subQuestion.id), subQuestion);
      }

      if (!subQuestion?.questionSourceId) return;
      surveySubQuestionsBySourceId.set(String(subQuestion.questionSourceId), subQuestion);
    });
  });

  return externalQuestions.flatMap((externalQuestion) => {
    const match =
      surveyQuestionsBySourceId.get(String(externalQuestion.id)) ||
      surveyQuestionsById.get(String(externalQuestion.id));
    const mergedQuestion = match
      ? {
          ...getMergedQuestion({
            externalQuestion,
            fields: SURVEY_QUESTION_FIELDS_TO_MERGE,
            surveyQuestion: match,
          }),
          id: match.questionSourceId || externalQuestion.id,
        }
      : externalQuestion;

    if (mergedQuestion.type !== QUESTION_TYPES.SEQUENTIAL_MONADIC.value) {
      return [mergedQuestion];
    }

    const surveySubQuestions = match?.subQuestions || [];
    const externalSubQuestions = externalQuestion.subQuestions || [];
    const subQuestions = externalSubQuestions.length
      ? externalSubQuestions.map((externalSubQuestion, index) => {
          const subQuestionMatch =
            surveySubQuestionsBySourceId.get(String(externalSubQuestion.id)) ||
            surveySubQuestionsById.get(String(externalSubQuestion.id)) ||
            surveySubQuestions[index];

          if (!subQuestionMatch) return externalSubQuestion;

          return {
            ...getMergedQuestion({
              externalQuestion: externalSubQuestion,
              fields: [...SURVEY_QUESTION_FIELDS_TO_MERGE, 'subQuestions', 'type'],
              surveyQuestion: subQuestionMatch,
            }),
            id: subQuestionMatch.questionSourceId || externalSubQuestion.id,
          };
        })
      : surveySubQuestions;

    return flattenSequentialMonadicQuestions([
      {
        ...mergedQuestion,
        concepts: match?.concepts || mergedQuestion.concepts || [],
        subQuestions,
      },
    ]);
  });
};
